import React, { useEffect, useRef, useState } from "react";
import { RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCRefLovProps, RCCServicesApi } from "react-common-components";
import DropdownWithBackground from "src/components/DropdownWithBackground";
import LovIncomeStatement from "src/components/Lov/IncomeStatement";
import { useTranslation } from "src/context/Translations/context";
import Loader from "src/components/Loader";
import { standardErrorMessages } from "src/utils/errors";
import { useAuth } from "src/context/Auth/context";
import { IRefIncomeStatementModals } from "../ModalIncomeStatement/interface";
import { ILovIncomeStatementWithOptions } from "./interface";
import ModalsIncomeStatementChangeCopyDelete from "../ModalIncomeStatement";
import { IRefIncomeStatementModalsLines } from "../ModalIncomeStatementLines/interface";
import ModalsIncomeStatementLines from "../ModalIncomeStatementLines";

const LovIncomeStatementWithOptions: React.FC<ILovIncomeStatementWithOptions> = ({
	budgetPlanId,
	control,
	setValue,
	type = 1
}) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { administrator, superUser } = useAuth();
	const lovRef = useRef<RCCRefLovProps>(null);
	const modalsIncomeStatementCopyChangeDeleteRef = useRef<IRefIncomeStatementModals>(null);
	const modalLinesIncomeRef = useRef<IRefIncomeStatementModalsLines>(null);
	const [hasMoreThanOneOption, setHasMoreThanOneOption] = useState(lovRef.current && lovRef.current?.getOptions().length > 1);

	const [hasPermissionsToEdit, setPermissions] = useState<boolean>(false);
	const [reloadLov, setReloadLov] = useState(false);
	const reload = () => {
		setReloadLov(true);
		window.setTimeout(() => {
			setReloadLov(false);
		}, 1000);
	};

	const loadData = async () => {	
		setReloadLov(true);
		if(administrator || superUser){
			setPermissions(true);
			setReloadLov(false);
			return;
		}		
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/SelectBudgetPlanPermissions",
				urlParameters: {
					budgetPlanId
				}
			});
			setPermissions(administrator || superUser || data.find((x: any) => x.ofp001Permissao === 1));
			setReloadLov(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
			setReloadLov(false);
		}
	};

	useEffect(() => {
		loadData();
	}, []);

	return (
		<>
			<RCCGrid container item alignItems="baseline" wrap="nowrap">
				{reloadLov ? <Loader /> : (
					<>
						<LovIncomeStatement 
							control={control}
							name="ofm001Id"
							label={translate("Demonstrativo")}
							extraParametersOnLoad={{
								budgetPlanId,
								type
							}}
							setValue={setValue}
							ref={lovRef}
						/>
						{hasPermissionsToEdit && (
							<DropdownWithBackground callerComponent={
								<RCCButton 
									color="grey"  
									icon={{
										name: "dotsVertical",
										color: "#FFF",
										size: 18
									}}
									onClick={() => setHasMoreThanOneOption(lovRef.current && lovRef.current?.getOptions().length > 1)}
								/>
							}
							position="leftEnd"
							styleInline={`
								>button{
    								transform: translateY(4px);
								}
							`}
							>
								<RCCDropDownItem
									selfEnclose
									onClick={() => {
										if(!lovRef.current)
											return;
										const firstValue = lovRef.current.getValue() as any;
										modalsIncomeStatementCopyChangeDeleteRef.current?.actionButtonChange(Number(firstValue[0].value));
									}}
								>
									<RCCIcon 
										name="pencil"
									/>
									{translate("Alterar")}
								</RCCDropDownItem>
								<RCCDropDownItem 
									selfEnclose
									onClick={() => {
										if(!lovRef.current)
											return;
										const firstValue = lovRef.current.getValue() as any;
										modalLinesIncomeRef.current?.actionButtonChange(Number(firstValue[0].value), firstValue[0].label);
									}}
								>
									<RCCIcon 
										name="settings" 
									/>
									{translate("Configurar")}
								</RCCDropDownItem>
								<RCCDropDownItem 
									selfEnclose
									onClick={() => {
										if(!lovRef.current)
											return;
										const firstValue = lovRef.current.getValue() as any;
										modalsIncomeStatementCopyChangeDeleteRef.current?.actionButtonCopy(Number(firstValue[0].value));
									}}
								>
									<RCCIcon 
										name="copy" 
									/>
									{translate("Copiar")}
								</RCCDropDownItem>
								{lovRef.current && hasMoreThanOneOption && (
									<>
										<RCCDropDownItem 
											selfEnclose
											onClick={() => {
												if(!lovRef.current)
													return;
												const firstValue = lovRef.current.getValue() as any;
												modalsIncomeStatementCopyChangeDeleteRef.current?.actionButtonChangeType(Number(firstValue[0].value));
											}}
										>
											<RCCIcon 
												name="refresh" 
											/>
											{translate("Alterar tipo")}
										</RCCDropDownItem>
										<RCCDropDownItem 
											selfEnclose
											onClick={() => {
												if(!lovRef.current)
													return;
												const firstValue = lovRef.current.getValue() as any;
												modalsIncomeStatementCopyChangeDeleteRef.current?.actionButtonDelete(Number(firstValue[0].value));
											}}
										>
											<RCCIcon 
												name="trash" 
											/>
											{translate("Excluir")}
										</RCCDropDownItem>
									</>
								)}
							</DropdownWithBackground>
						)}
					</>
				)}
			</RCCGrid>

			<ModalsIncomeStatementChangeCopyDelete 
				ref={modalsIncomeStatementCopyChangeDeleteRef}
				budgetPlanId={budgetPlanId}
				onCallback={reload}
				type={type}
			/>

			<ModalsIncomeStatementLines 
				ref={modalLinesIncomeRef}
				budgetPlanId={budgetPlanId}
			/>
		</>
	);
};

export default LovIncomeStatementWithOptions;
